// primitives.tsx — shared building blocks for the Drip Campaigns redesign.
//
// Ported from the design handoff's `prototype/src/drip-primitives.jsx`. These
// are leaf presentation components: the prototype's inline-style approach is
// kept verbatim (fastest path to pixel fidelity — translating to MUI is not
// required for primitives). The only substantive change is swapping the
// prototype's inline-SVG `Icons.*` for `lucide-react` (names map 1:1).
//
// All components are designed to render inside a `.campaigns-rd` wrapper, which
// supplies the design tokens (see styles/campaigns-redesign.css).

import React from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { namespace } from '@common/config/constants';
import {
  Users,
  Clock,
  Check,
  Image as ImageIcon,
  Film,
  BarChart3,
  CornerDownRight,
  Link as LinkIcon,
  Reply,
  PlayCircle,
  PauseCircle,
  StopCircle,
  CheckCircle,
  Pencil,
  Dot,
  CalendarX,
  type LucideIcon,
} from 'lucide-react';

// ─── Audience colour tints ───
// These were literal light-mode hex, read straight into inline styles, so
// NOTHING flipped them in dark mode: every audience tile on the list and every
// step badge in the wizard stayed a bright pastel block on a near-black page.
// They point at tokens now (defined light-first in campaigns-redesign.css, with
// a `[data-theme="dark"]` override), so the theme owns the values and the
// component API is unchanged.
//
// `glow` is the tint's soft drop, pre-mixed as an rgba token: the step badge
// used to build one by concatenating an alpha suffix onto the hex string, which
// is not something a `var()` can be asked to do.
export interface Tint { bg: string; fg: string; line: string; glow: string }
const tint = (name: string): Tint => ({
  bg: `var(--tint-${name}-bg)`,
  fg: `var(--tint-${name}-fg)`,
  line: `var(--tint-${name}-line)`,
  glow: `var(--tint-${name}-glow)`,
});
export const TINTS: Record<string, Tint> = {
  amber: tint('amber'),
  violet: tint('violet'),
  blue: tint('blue'),
  green: tint('green'),
  rose: tint('rose'),
  cyan: tint('cyan'),
};

// ─── Toggle ───
export interface ToggleProps {
  value: boolean;
  onChange: (next: boolean) => void;
  size?: 'sm' | 'md';
}
export const Toggle: React.FC<ToggleProps> = ({ value, onChange, size = 'md' }) => {
  const w = size === 'sm' ? 30 : 36;
  const h = size === 'sm' ? 17 : 20;
  const k = h - 4;
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      aria-pressed={value}
      style={{
        appearance: 'none', border: 0, cursor: 'pointer', flexShrink: 0,
        width: w, height: h, borderRadius: 999, padding: 0,
        background: value ? 'var(--bt-blue)' : 'var(--line-strong)',
        position: 'relative', transition: 'background .15s',
      }}
    >
      <span style={{
        // Logical, not physical: `left` put the knob on the same side in Hebrew,
        // so a Hebrew reader saw an "on" toggle sitting where "off" belongs. If a
        // browser will not animate the logical inset it still lands correctly —
        // it just snaps, which beats being on the wrong side.
        position: 'absolute', top: 2, insetInlineStart: value ? w - k - 2 : 2,
        width: k, height: k, borderRadius: '50%', background: 'white',
        transition: 'inset-inline-start .16s cubic-bezier(.3,.8,.3,1)', boxShadow: '0 1px 2px rgba(0,0,0,.25)',
      }} />
    </button>
  );
};

// ─── Audience tile avatar ───
export interface AudAvatarProps {
  color?: string;
  size?: number;
  icon?: LucideIcon;
}
export const AudAvatar: React.FC<AudAvatarProps> = ({ color = 'blue', size = 36, icon }) => {
  const t = TINTS[color] || TINTS.blue;
  const I = icon || Users;
  return (
    <div style={{
      width: size, height: size, borderRadius: 9, flexShrink: 0,
      background: t.bg, color: t.fg, border: `1px solid ${t.line}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <I size={size * 0.46} />
    </div>
  );
};

// ─── Status label (campaign lifecycle) ───
export type CampaignStatus =
  | 'draft' | 'pending' | 'scheduled' | 'active' | 'running' | 'paused' | 'completed' | 'aborted' | 'expired';

export interface StatusMapEntry {
  /** i18n key for `label`. The map is built at module load, where no `t` exists,
   *  so the word has to travel as a key and be resolved at render. */
  labelKey: string;
  label: string;
  cls: 'neutral' | 'brand' | 'success' | 'kbmiss' | 'escalate';
  icon: LucideIcon;
}

export const STATUS_MAP: Record<CampaignStatus, StatusMapEntry> = {
  draft: { labelKey: 'campaignsRedesign.campaignStatus.draft', label: 'Draft', cls: 'neutral', icon: Pencil },
  pending: { labelKey: 'campaignsRedesign.campaignStatus.scheduled', label: 'Scheduled', cls: 'brand', icon: Clock },
  // A launched-but-not-yet-started drip/campaign whose first send is still in the
  // future — "Scheduled", not the misleading "Running · 0 sent". Same look as pending.
  scheduled: { labelKey: 'campaignsRedesign.campaignStatus.scheduled', label: 'Scheduled', cls: 'brand', icon: Clock },
  active: { labelKey: 'campaignsRedesign.campaignStatus.running', label: 'Running', cls: 'success', icon: PlayCircle },
  running: { labelKey: 'campaignsRedesign.campaignStatus.running', label: 'Running', cls: 'success', icon: PlayCircle },
  paused: { labelKey: 'campaignsRedesign.campaignStatus.paused', label: 'Paused', cls: 'kbmiss', icon: PauseCircle },
  // A finished campaign/drip is a SUCCESS, not a muted neutral end-state: an
  // all-`confirmed` send (e.g. a group send that can never device-deliver → 0
  // delivered/read) is done and green, never a stuck/grey "Completed". Green
  // treatment matches the StepMeter's full-green confirmed bar + the done step node.
  completed: { labelKey: 'campaignsRedesign.campaignStatus.completed', label: 'Completed', cls: 'success', icon: CheckCircle },
  aborted: { labelKey: 'campaignsRedesign.campaignStatus.stopped', label: 'Stopped', cls: 'escalate', icon: StopCircle },
  // FE#256: a non-recurring campaign whose run finished but whose status the backend never advanced
  // (it lingered at `running`). Rendered as a muted, terminal "Expired" — honest that the run is over
  // without over-claiming a fully-successful send (unlike green Completed).
  expired: { labelKey: 'campaignsRedesign.campaignStatus.expired', label: 'Expired', cls: 'neutral', icon: CalendarX },
};

export interface StatusLabelProps {
  status: CampaignStatus | string;
}
export const StatusLabel: React.FC<StatusLabelProps> = ({ status }) => {
  const { t } = useTranslation(namespace);
  const s = STATUS_MAP[status as CampaignStatus] || STATUS_MAP.draft;
  const I = s.icon || Dot;
  return (
    <span className={`pill ${s.cls}`}>
      <I size={11} /> {t(s.labelKey, s.label)}
    </span>
  );
};

// ─── Message asset badges ───
export type AssetKind = 'image' | 'video' | 'poll' | 'buttons' | 'link';

const MEDIA_META: Record<AssetKind, { icon: LucideIcon; labelKey: string; label: string }> = {
  image: { icon: ImageIcon, labelKey: 'campaignsRedesign.asset.image', label: 'Image' },
  video: { icon: Film, labelKey: 'campaignsRedesign.asset.video', label: 'Video' },
  poll: { icon: BarChart3, labelKey: 'campaignsRedesign.asset.poll', label: 'Poll' },
  buttons: { icon: CornerDownRight, labelKey: 'campaignsRedesign.asset.buttons', label: 'Buttons' },
  link: { icon: LinkIcon, labelKey: 'campaignsRedesign.asset.link', label: 'Link' },
};

export interface AssetBadgeProps {
  kind: AssetKind | string;
}
export const AssetBadge: React.FC<AssetBadgeProps> = ({ kind }) => {
  const { t } = useTranslation(namespace);
  const m = MEDIA_META[kind as AssetKind];
  if (!m) return null;
  const I = m.icon;
  return (
    // Same chip grammar as `.pill` (see campaigns-redesign.css): one quiet
    // ground, no border, the glyph carrying whatever colour there is. It used
    // to be the only bordered chip on the card, which made a media badge look
    // like a different KIND of thing from the status beside it.
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10.5, color: 'var(--muted)',
      background: 'var(--chip-surface)', borderRadius: 999, paddingBlock: 2, paddingInline: '6px 8px',
    }}>
      <I size={11} /> {t(m.labelKey, m.label)}
    </span>
  );
};

// ─── Delay control (between steps) ───
export type DelayUnit = 'minute' | 'hour' | 'day' | 'week';
const UNITS: DelayUnit[] = ['minute', 'hour', 'day', 'week'];

/**
 * "min" + an "s" when the number isn't 1 was English grammar written in code:
 * one plural rule, applied to every language, for a control that sits in the
 * middle of the campaign wizard. i18next decides the form now — which is the
 * only way Hebrew's DUAL ("יומיים" for two days, a word with no 2 in it) can
 * ever appear. `t` is passed in rather than hooked because `gapText` is called
 * from render helpers and pure formatters, not only from components.
 */
const UNIT_KEY: Record<DelayUnit, { labelKey: string; one: string; other: string }> = {
  minute: { labelKey: 'campaignsRedesign.delayUnit.minute', one: 'min', other: 'mins' },
  hour: { labelKey: 'campaignsRedesign.delayUnit.hour', one: 'hour', other: 'hours' },
  day: { labelKey: 'campaignsRedesign.delayUnit.day', one: 'day', other: 'days' },
  week: { labelKey: 'campaignsRedesign.delayUnit.week', one: 'week', other: 'weeks' },
};
export const unitLabel = (t: TFunction, u: DelayUnit, n: number): string =>
  t(UNIT_KEY[u].labelKey, n === 1 ? UNIT_KEY[u].one : UNIT_KEY[u].other, { count: n });

export interface DelayGap {
  value: number;
  unit: DelayUnit;
}

export interface DelayControlProps {
  gap?: DelayGap | null;
  onChange: (next: DelayGap) => void;
  compact?: boolean;
  /** Leading word before the number. Defaults to the translated 'Wait' (wizard);
   *  the detail reuses this same control as 'Send in … from now'. */
  leadLabel?: string;
  /** Trailing text after the pills (hidden when `compact`). Default is the
   *  wizard's "after the previous message completes". */
  suffix?: React.ReactNode;
  /** Static, non-editable rendering — same dashed pill, but the number + unit
   *  are plain text (no input / segmented buttons). The campaign detail reuses
   *  this to SHOW the historical wait between already-sent steps. */
  readOnly?: boolean;
}
export const DelayControl: React.FC<DelayControlProps> = ({ gap, onChange, compact, leadLabel, suffix, readOnly }) => {
  const { t } = useTranslation(namespace);
  const v = gap?.value ?? 2;
  const u = gap?.unit ?? 'day';
  const lead = leadLabel ?? t('campaignsRedesign.delayUnit.wait', 'Wait');
  const tail = suffix ?? t('campaignsRedesign.delayUnit.afterPrevious', 'after the previous message completes');
  if (readOnly) {
    return (
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        background: 'var(--surface)', border: '1px dashed var(--bt-blue-line)', borderRadius: 999,
        padding: '5px 12px',
      }}>
        <Clock size={13} stroke="var(--bt-blue-deep)" />
        <span style={{ fontSize: 12, color: 'var(--muted)' }}>{lead}</span>
        <span className="num" style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ink)' }}>{v}</span>
        <span style={{ fontSize: 12, color: 'var(--ink-2)', fontWeight: 500 }}>{unitLabel(t, u, v)}</span>
        {!compact && (
          <span style={{ fontSize: 11.5, color: 'var(--muted-2)' }}>
            {tail}
          </span>
        )}
      </div>
    );
  }
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 8,
      background: 'var(--surface)', border: '1px dashed var(--bt-blue-line)', borderRadius: 999,
      padding: '5px 6px 5px 12px',
    }}>
      <Clock size={13} stroke="var(--bt-blue-deep)" />
      <span style={{ fontSize: 12, color: 'var(--muted)' }}>{lead}</span>
      <input
        className="num"
        type="number"
        min={1}
        value={v}
        onChange={(e) => onChange({ value: Math.max(1, Number(e.target.value) || 1), unit: u })}
        style={{
          width: 42, textAlign: 'center', border: '1px solid var(--line)', borderRadius: 6,
          background: 'var(--surface-2)', padding: '3px 4px', fontSize: 12.5, color: 'var(--ink)', outline: 'none',
        }}
      />
      <div className="seg" style={{ padding: 2 }}>
        {UNITS.map((un) => (
          <button
            type="button"
            key={un}
            className="seg-opt"
            data-active={u === un}
            style={{ padding: '3px 7px', fontSize: 11 }}
            onClick={() => onChange({ value: v, unit: un })}
          >
            {unitLabel(t, un, 2)}
          </button>
        ))}
      </div>
      {!compact && (
        <span style={{ fontSize: 11.5, color: 'var(--muted-2)', paddingInlineEnd: 6 }}>
          {tail}
        </span>
      )}
    </div>
  );
};

// Human-readable gap
export const gapText = (t: TFunction, gap?: DelayGap | null): string =>
  gap ? t('campaignsRedesign.delayUnit.gap', '{{n}} {{unit}}', { n: gap.value, unit: unitLabel(t, gap.unit, gap.value) }) : '';

// ─── Message preview (WhatsApp bubble) ───
// Faithful to the prototype's loose preview shape. D2-D4 adapt the real
// bt-common Template into this presentation-only shape.
export interface PreviewMedia {
  kind: 'image' | 'video' | string;
  name?: string;
  url?: string;
}
export interface PreviewPoll {
  question: string;
  options: string[];
}
export interface PreviewButton {
  label: string;
}
export interface PreviewTemplate {
  text?: string;
  media?: PreviewMedia | null;
  poll?: PreviewPoll | null;
  buttons?: PreviewButton[];
}

export interface MessagePreviewProps {
  template: PreviewTemplate;
  name?: string;
  compact?: boolean;
  /** Cap the rendered media height (px). Defaults to 220 (full bubble, as the
   *  wizard/detail surfaces want). The Templates management list passes a small
   *  value so an image row stays bounded next to its name/snippet instead of
   *  ballooning the row to the image's full height. */
  mediaMaxHeight?: number;
}
export const MessagePreview: React.FC<MessagePreviewProps> = ({ template, compact, mediaMaxHeight = 220 }) => {
  const { t } = useTranslation(namespace);
  const { text, media, poll, buttons } = template;
  return (
    <div className="wa-bubble" style={{ maxWidth: compact ? '100%' : 320 }}>
      {media && (
        // A real image asset renders the actual picture; video (no thumbnail URL)
        // and a URL-less asset fall back to the icon placeholder + filename label.
        media.kind !== 'video' && media.url ? (
          <div style={{ borderRadius: 6, overflow: 'hidden', marginBottom: 6, border: '1px solid rgba(0,0,0,.06)' }}>
            <img src={media.url} alt={media.name ?? ''} style={{ display: 'block', width: '100%', maxHeight: mediaMaxHeight, objectFit: 'cover' }} />
          </div>
        ) : (
          <div style={{
            borderRadius: 6, overflow: 'hidden', marginBottom: 6, background: 'rgba(0,0,0,.06)',
            height: media.kind === 'video' ? 110 : 90, display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '1px solid rgba(0,0,0,.06)', position: 'relative',
          }}>
            {media.kind === 'video'
              ? <PlayCircle size={30} stroke="#0b1220" />
              : <ImageIcon size={26} stroke="#0b1220" />}
            {media.name && (
              <span style={{
                position: 'absolute', bottom: 5, insetInlineStart: 6, fontSize: 9.5,
                background: 'rgba(0,0,0,.55)', color: 'white', padding: '1px 5px', borderRadius: 4,
              }}>
                {media.name}
              </span>
            )}
          </div>
        )
      )}
      {/* Only render the text line when there's actual text — otherwise a poll/
          media-only bubble shows renderTokens' "Empty message" placeholder. */}
      {text && text.trim() ? <div style={{ whiteSpace: 'pre-wrap' }}>{renderTokens(text, t)}</div> : null}
      {poll && (
        <div style={{ marginTop: 7, padding: 7, background: 'rgba(255,255,255,.5)', borderRadius: 6, border: '1px solid rgba(0,0,0,.06)' }}>
          <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{poll.question}</div>
          {poll.options.map((o, i) => (
            <div key={i} style={{ fontSize: 11, padding: '3px 6px', background: 'white', borderRadius: 4, marginTop: 3, border: '1px solid rgba(0,0,0,.06)' }}>
              {o}
            </div>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end', marginTop: 3, fontSize: 9.5, color: 'rgba(11,18,32,.45)' }}>
        <span className="num">9:41</span><Check size={11} stroke="var(--wa-deep)" />
      </div>
      {buttons && buttons.length > 0 && (
        <div style={{ marginTop: 6, marginInline: -10, marginBottom: -6, borderTop: '1px solid rgba(0,0,0,.08)' }}>
          {buttons.map((b, i) => (
            <div key={i} style={{
              textAlign: 'center', padding: '7px', fontSize: 12, color: 'var(--bt-blue-mark)', fontWeight: 500,
              borderTop: i ? '1px solid rgba(0,0,0,.08)' : 'none',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            }}>
              <Reply size={12} /> {b.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Adapt a bt-common campaign `Template` (the real editor's write-shape) into
// the loose presentation-only `PreviewTemplate` consumed by `MessagePreview`
// and the at-a-glance summaries. Mirrors `badgesForSteps` in management-list
// (asset.type → image/video, poll, buttons) so the two stay in lock-step.
export function templateToPreview(tpl?: {
  message?: string | null;
  asset?: { url?: string | null; type?: string | null; name?: string | null } | null;
  isPoll?: boolean;
  poll?: unknown;
  buttons?: unknown;
} | null): PreviewTemplate {
  if (!tpl) return { text: '' };
  const assetType = tpl.asset?.type ?? '';
  const media: PreviewMedia | null = tpl.asset?.url
    ? { kind: /video/i.test(assetType) ? 'video' : 'image', name: tpl.asset.name ?? undefined, url: tpl.asset.url ?? undefined }
    : null;
  const pollObj = tpl.poll as { question?: string; name?: string; options?: unknown[] } | null | undefined;
  const poll: PreviewPoll | null = (tpl.isPoll || pollObj)
    ? {
        question: pollObj?.question ?? pollObj?.name ?? '',
        options: Array.isArray(pollObj?.options)
          ? pollObj!.options.map((o) => (typeof o === 'string' ? o : ((o as { text?: string })?.text ?? '')))
          : [],
      }
    : null;
  const buttonList = Array.isArray(tpl.buttons) ? (tpl.buttons as { value?: string }[]) : [];
  const buttons: PreviewButton[] = buttonList
    .map((b) => ({ label: b?.value ?? '' }))
    .filter((b) => b.label);
  return { text: tpl.message ?? '', media, poll, buttons };
}

// Collapse a double-brace `{{token}}` to a single-brace `{token}` so a Handlebars
// habit still resolves. MUST stay identical to the send-time collapse in the
// backend resolver (campaign-variable-substitution.substituteVariables) so the
// preview and the actual send agree. Balanced `{{ … }}` only; single-brace and
// `{{}}` (empty) are left untouched.
export const collapseDoubleBraces = (text: string): string =>
  text.replace(/\{\{([^{}]+)\}\}/g, '{$1}');

// Render {tokens} as chips
export function renderTokens(
  text: string | undefined,
  // REQUIRED, like `unitLabel`/`gapText` in this file: a default would let a new
  // call site render the placeholder in English and nothing would say so.
  t: (key: string, defaultValue: string) => string,
): React.ReactNode {
  if (!text) return <span style={{ color: 'rgba(11,18,32,.4)' }}>{t('campaignsRedesign.emptyMessage', 'Empty message')}</span>;
  // Tokens are SINGLE-brace `{First Name}` (matches the token buttons + the
  // send-time matcher + findUnknownTokens). A double-brace `{{x}}` (Handlebars
  // habit) is collapsed to `{x}` first, so it chips + resolves like the single
  // form — the backend resolver applies the same collapse at send.
  const parts = collapseDoubleBraces(String(text)).split(/(\{[^{}]+\})/);
  return parts.map((p, i) =>
    /^\{[^{}]+\}$/.test(p)
      ? (
        <span key={i} style={{
          background: 'var(--bt-blue-soft)', color: 'var(--bt-blue-deep)', borderRadius: 4,
          padding: '0 3px', fontSize: '.92em', fontWeight: 500,
        }}>
          {p.replace(/[{}]/g, '')}
        </span>
      )
      : <React.Fragment key={i}>{p}</React.Fragment>
  );
}

// ─── List loading skeleton ───
// Shimmer placeholder for the management lists (Audiences / Templates /
// Campaigns) while their collection's first load is in flight. Prevents the
// "empty state flashes, then fills" on a cold reload — the lists show this
// until the reducer's `itemsLoaded` flips true, then either the rows or the
// genuine empty state. Uses the shared `.skel` shimmer (campaigns-redesign.css)
// and mirrors the card-row shape (name/snippet on the left, a preview block on
// the right) so the transition to real rows is calm.
export interface ListSkeletonProps {
  rows?: number;
}
export const ListSkeleton: React.FC<ListSkeletonProps> = ({ rows = 4 }) => {
  const { t } = useTranslation(namespace);
  return (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }} aria-busy="true" aria-label={t('common.loading', 'Loading...')}>
    {Array.from({ length: rows }).map((_, i) => (
      <div
        key={i}
        style={{
          border: '1px solid var(--line)', borderRadius: 12, background: 'var(--surface)',
          padding: '15px', display: 'flex', alignItems: 'center', gap: 14,
        }}
      >
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div className="skel" style={{ height: 13, width: '34%', borderRadius: 6 }} />
          <div className="skel" style={{ height: 11, width: `${52 - (i % 3) * 8}%`, borderRadius: 6 }} />
        </div>
        <div className="skel" style={{ height: 42, width: 168, borderRadius: '4px 12px 12px 12px', flexShrink: 0 }} />
      </div>
    ))}
  </div>
  );
};

// ─── Section eyebrow ───
export interface EyebrowProps {
  children: React.ReactNode;
  style?: React.CSSProperties;
}
export const Eyebrow: React.FC<EyebrowProps> = ({ children, style }) => (
  <div style={{
    fontSize: 11, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase',
    color: 'var(--muted-2)', ...style,
  }}>
    {children}
  </div>
);
